import { useMemo, useState } from "react";
import { Search, Plus } from "lucide-react";
import { Input } from "../ui/input";
import { Button } from "../ui/button";

export default function PatientSidebar({
  patients,
  selectedPatientId,
  onSelect,
  onNewPlan,
}) {
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return patients;
    return patients.filter(
      (p) =>
        p.id.toLowerCase().includes(q) ||
        p.initials.toLowerCase().includes(q)
    );
  }, [patients, query]);

  return (
    <div
      data-testid="patient-sidebar"
      className="bg-white rounded-2xl border border-slate-200/80 shadow-soft p-4 sticky top-20"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="font-outfit text-sm font-semibold text-[#0F172A]">
          Patients
        </div>
        <span className="font-figtree text-xs text-[#94A3B8]">
          {patients.length}
        </span>
      </div>

      <div className="relative mb-3">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
        <Input
          data-testid="sidebar-search-input"
          placeholder="Search by ID or initials"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="pl-9 h-10 rounded-xl bg-[#F8FAFC] border-slate-200 focus-visible:ring-[#10B981] font-figtree text-sm"
        />
      </div>

      <Button
        data-testid="sidebar-new-plan-btn"
        onClick={onNewPlan}
        className="w-full rounded-xl bg-[#10B981] hover:bg-[#059669] text-white font-figtree text-sm font-medium mb-3"
      >
        <Plus className="w-4 h-4 mr-2" />
        New plan
      </Button>

      <div className="space-y-1.5 max-h-[60vh] overflow-y-auto pr-1">
        {filtered.length === 0 ? (
          <div className="text-center py-6 font-figtree text-xs text-[#94A3B8]">
            No matching patients.
          </div>
        ) : (
          filtered.map((p) => {
            const isActive = p.id === selectedPatientId;
            return (
              <button
                key={p.id}
                data-testid={`sidebar-patient-${p.id}`}
                onClick={() => onSelect(p.id)}
                className={`w-full text-left rounded-xl px-3 py-2.5 flex items-center gap-3 transition-all ${
                  isActive
                    ? "bg-[#D1FAE5] border border-[#10B981]/40"
                    : "hover:bg-slate-50 border border-transparent"
                }`}
              >
                <span
                  className={`inline-flex items-center justify-center w-9 h-9 rounded-full font-outfit text-xs font-bold ${
                    isActive
                      ? "bg-[#10B981] text-white"
                      : "bg-slate-100 text-[#475569]"
                  }`}
                >
                  {p.initials}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="font-outfit text-sm font-semibold text-[#0F172A]">
                    {p.id}
                  </div>
                  <div className="font-figtree text-xs text-[#64748B]">
                    {(p.plans?.length ?? p.plan_count ?? 0)}{" "}
                    {(p.plans?.length ?? p.plan_count ?? 0) === 1 ? "plan" : "plans"}
                  </div>
                </div>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}
